import React, { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";

const DEFAULT_ALPHA_ROWS = [
  ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"],
  ["a", "s", "d", "f", "g", "h", "j", "k", "l"],
  ["SHIFT", "z", "x", "c", "v", "b", "n", "m", "BACKSPACE"],
  ["123", "SPACE", "@", ".", "-", "_", "CLEAR", "DONE"],
];

const DEFAULT_NUMERIC_ROWS = [
  ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
  ["+", "-", "(", ")", "/", "#", "*", "BACKSPACE"],
  ["ABC", "SPACE", ".", "@", "_", "CLEAR", "DONE"],
];

const isSingleChar = (key) => typeof key === "string" && key.length === 1;

const keyLabel = (key, doneLabel) => {
  if (key === "SHIFT") return "Caps";
  if (key === "BACKSPACE") return "Backspace";
  if (key === "SPACE") return "Space";
  if (key === "CLEAR") return "Clear";
  if (key === "DONE") return doneLabel || "Done";
  if (key === "ABC") return "ABC";
  if (key === "123") return "123";
  return key;
};

const keyClassName = (key, size, { shift, mode }) => {
  const sizeClass =
    size === "lg"
      ? "h-16 text-lg"
      : size === "sm"
        ? "h-10 text-[13px]"
        : "h-12 text-sm";
  const base =
    `${sizeClass} rounded-2xl border font-semibold select-none active:scale-[0.98] transition-all duration-150 shadow-sm`;
  const surface =
    "bg-white/90 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100";
  const hover =
    "hover:-translate-y-px hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-md";

  if (key === "SHIFT" && mode === "alpha" && shift) {
    return `${base} border-indigo-300 bg-gradient-to-br from-indigo-600 to-violet-600 text-white shadow-[0_10px_25px_rgba(99,102,241,0.35)]`;
  }

  if (key === "DONE") {
    return `${base} border-indigo-500 bg-gradient-to-br from-indigo-600 to-violet-600 text-white shadow-[0_10px_25px_rgba(99,102,241,0.35)] hover:from-indigo-700 hover:to-violet-700`;
  }

  if (key === "CLEAR") {
    return `${base} bg-rose-50 dark:bg-rose-950/40 hover:bg-rose-100 dark:hover:bg-rose-950/60 border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-200`;
  }

  return `${base} ${surface} ${hover}`;
};

const keyFlexClass = (key) => {
  if (key === "SPACE") return "flex-[3]";
  if (key === "SHIFT") return "flex-[1.5]";
  if (key === "BACKSPACE") return "flex-[1.5]";
  if (key === "CLEAR") return "flex-[1.5]";
  if (key === "DONE") return "flex-[1.5]";
  if (key === "123" || key === "ABC") return "flex-[1.25]";
  return "flex-1";
};

const OnScreenKeyboard = ({
  value,
  onChange,
  inputRef,
  onDone,
  onEnter,
  layout = "alpha",
  size = "md",
  title,
  className = "",
  disabled = false,
  doneLabel = "Done",
  showDone = true,
  showModeIndicator = true,
  compact = false,
}) => {
  const [mode, setMode] = useState(layout === "numeric" ? "numeric" : "alpha");
  const [shift, setShift] = useState(false);
  const selectionRef = useRef(null);

  useEffect(() => {
    setMode(layout === "numeric" ? "numeric" : "alpha");
    setShift(false);
  }, [layout]);

  const rows = useMemo(
    () => (mode === "numeric" ? DEFAULT_NUMERIC_ROWS : DEFAULT_ALPHA_ROWS),
    [mode],
  );

  const displayRows = useMemo(() => {
    if (mode !== "alpha" || !shift) {
      return rows.map((row) =>
        showDone ? row : row.filter((key) => key !== "DONE"),
      );
    }

    return rows.map((row) =>
      row
        .filter((key) => showDone || key !== "DONE")
        .map((key) => (isSingleChar(key) ? key.toUpperCase() : key)),
    );
  }, [mode, rows, shift, showDone]);

  const captureSelection = () => {
    const input = inputRef?.current;
    if (!input || typeof input.selectionStart !== "number") {
      selectionRef.current = null;
      return;
    }

    selectionRef.current = {
      input,
      start: input.selectionStart,
      end: input.selectionEnd ?? input.selectionStart,
    };
  };

  const getSelection = () => {
    const currentValue = String(value || "");
    const input = inputRef?.current;
    const savedSelection = selectionRef.current;

    if (input && savedSelection?.input === input) {
      return {
        input,
        start: Math.min(savedSelection.start, currentValue.length),
        end: Math.min(savedSelection.end, currentValue.length),
      };
    }

    return {
      input: null,
      start: currentValue.length,
      end: currentValue.length,
    };
  };

  const applyChange = (nextValue, caretPosition) => {
    if (disabled) return;
    const input = inputRef?.current;

    // Commit the controlled value before moving the caret so rapid taps cannot
    // capture a selection position from the previous render.
    if (onChange) {
      flushSync(() => onChange(nextValue));
    }

    if (!input || !Number.isFinite(caretPosition)) return;

    const nextCaretPosition = Math.min(caretPosition, input.value.length);
    selectionRef.current = {
      input,
      start: nextCaretPosition,
      end: nextCaretPosition,
    };

    try {
      input.focus({ preventScroll: true });
    } catch {
      input.focus();
    }
    input.setSelectionRange(nextCaretPosition, nextCaretPosition);
  };

  const insertAtSelection = (text) => {
    const currentValue = String(value || "");
    const { start, end } = getSelection();
    applyChange(
      `${currentValue.slice(0, start)}${text}${currentValue.slice(end)}`,
      start + text.length,
    );
  };

  const handleKey = (key) => {
    if (disabled) return;

    if (key === "SHIFT") {
      setShift((prev) => !prev);
      return;
    }

    if (key === "123") {
      setMode("numeric");
      setShift(false);
      return;
    }

    if (key === "ABC") {
      setMode("alpha");
      return;
    }

    if (key === "BACKSPACE") {
      const currentValue = String(value || "");
      const { start, end } = getSelection();
      if (start !== end) {
        applyChange(
          `${currentValue.slice(0, start)}${currentValue.slice(end)}`,
          start,
        );
      } else if (start > 0) {
        applyChange(
          `${currentValue.slice(0, start - 1)}${currentValue.slice(end)}`,
          start - 1,
        );
      }
      return;
    }

    if (key === "SPACE") {
      insertAtSelection(" ");
      return;
    }

    if (key === "CLEAR") {
      applyChange("", 0);
      return;
    }

    if (key === "DONE") {
      if (onDone) {
        onDone();
      } else if (onEnter) {
        onEnter();
      }
      return;
    }

    if (isSingleChar(key)) {
      const nextChar =
        mode === "alpha" && shift ? key.toUpperCase() : String(key);
      insertAtSelection(nextChar);
    }
  };

  return (
    <div
      className={[
        compact
          ? "rounded-[24px] border border-slate-200/80 bg-gradient-to-br from-slate-50 to-white p-2.5 shadow-[0_20px_60px_rgba(15,23,42,0.08)] dark:border-slate-700 dark:from-slate-900 dark:to-slate-800"
          : "rounded-[28px] border border-slate-200/80 bg-gradient-to-br from-slate-50 to-white p-4 shadow-[0_20px_60px_rgba(15,23,42,0.08)] dark:border-slate-700 dark:from-slate-900 dark:to-slate-800",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className={compact ? "space-y-1.5" : "space-y-2"}>
        {displayRows.map((row, rowIndex) => (
          <div key={`row-${rowIndex}`} className={compact ? "flex gap-1.5" : "flex gap-2"}>
            {row.map((key, keyIndex) => (
              <button
                key={`${rowIndex}-${keyIndex}-${key}`}
                type="button"
                className={[
                  keyClassName(key, size, { shift, mode }),
                  keyFlexClass(key),
                ]
                  .filter(Boolean)
                  .join(" ")}
                onPointerDown={captureSelection}
                onClick={() => handleKey(key)}
                disabled={disabled}
              >
                {keyLabel(key, doneLabel)}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default OnScreenKeyboard;
